import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LuLandmark,
  LuClipboardList,
  LuTrendingUp,
  LuArrowUpRight,
  LuArrowDownRight,
  LuMinus,
} from 'react-icons/lu';
import { COPY } from '../../contracts/copyMap';

// ScanRevealModal — the "test results" reveal (design-recreation §reveal).
// Staged: ANALYZING spinner → score counts up around a filling gold dial →
// burst + percentile/delta chips → VIEW FULL SCORE / REVIEW ROUTINE / Dismiss.
//
//   stage 0 analyzing · stage 1 counting · stage 2 revealed

const RING_CIRCUMFERENCE = 534.07; // 2πr, r=85
const ANALYZE_MS = 1100;
const COUNT_MS = 1500;

// Delta chip tone, keyed by direction.
//
// This chip was hardcoded green-with-an-up-arrow, which meant a DECLINE would
// have rendered as green progress the moment the delta was wired up (it never
// was — Create.js passed deltaText={null} since launch, so it has shown to
// nobody). A score the user cannot trust is worse than no score, and the score
// is the entire product.
const DELTA_TONE = {
  up: { color: '#8FD2A0', bg: 'rgba(143,210,160,0.08)', border: 'rgba(143,210,160,0.28)', Icon: LuArrowUpRight },
  down: { color: '#D8A0A0', bg: 'rgba(216,160,160,0.08)', border: 'rgba(216,160,160,0.28)', Icon: LuArrowDownRight },
  flat: { color: '#B8B2A6', bg: 'rgba(184,178,166,0.08)', border: 'rgba(184,178,166,0.24)', Icon: LuMinus },
};

const ScanRevealModal = ({
  open,
  score10,            // number, e.g. 8.4 (overall_score / 10)
  percentileTopText,  // string like "Top 12%" or null
  deltaText,          // string like "+0.4 since last scan" or null
  deltaDirection = 'up', // 'up' | 'down' | 'flat' — drives the chip's tone
  onViewScore,
  onReviewRoutine,
  onDismiss,
}) => {
  const target = typeof score10 === 'number' && Number.isFinite(score10) ? score10 : 0;
  const [stage, setStage] = useState(0);
  const [scoreNow, setScoreNow] = useState(0);
  const timers = useRef([]);
  const raf = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    // reset + run the staged sequence
    setStage(0);
    setScoreNow(0);
    const clearAll = () => {
      timers.current.forEach(clearTimeout);
      timers.current = [];
      if (raf.current) cancelAnimationFrame(raf.current);
    };

    // Stage progression is timer-driven (robust under fake timers / no rAF).
    // The rAF loop only smooths the counting number; the final value + reveal
    // are guaranteed by absolute timeouts.
    const t1 = setTimeout(() => {
      setStage(1);
      const start = typeof performance !== 'undefined' ? performance.now() : Date.now();
      const tick = (now) => {
        const p = Math.min(1, (now - start) / COUNT_MS);
        const eased = 1 - Math.pow(1 - p, 3);
        setScoreNow(Math.round(target * eased * 10) / 10);
        if (p < 1 && typeof requestAnimationFrame === 'function') {
          raf.current = requestAnimationFrame(tick);
        }
      };
      if (typeof requestAnimationFrame === 'function') {
        raf.current = requestAnimationFrame(tick);
      }
    }, ANALYZE_MS);
    const tReveal = setTimeout(() => {
      setScoreNow(target);
      setStage(2);
    }, ANALYZE_MS + COUNT_MS + 220);
    timers.current.push(t1, tReveal);

    return clearAll;
  }, [open, target]);

  const ringOffset = RING_CIRCUMFERENCE * (1 - scoreNow / 10);
  const analyzing = stage === 0;
  const revealed = stage === 2;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[80] flex items-center justify-center px-5"
          style={{ background: 'rgba(0,0,0,0.82)' }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.24 }}
          onClick={onDismiss}
          role="dialog"
          aria-modal="true"
          aria-label={COPY.REVEAL_DONE_HEADLINE}
        >
          <motion.div
            className="relative w-full max-w-[460px] rounded-card p-10 pb-8 text-center overflow-hidden"
            style={{
              background: 'linear-gradient(180deg, #1B1B1A, #0f0f0e)',
              border: '1px solid rgba(201,164,92,0.28)',
              boxShadow: '0 40px 100px -20px rgba(0,0,0,0.9)',
            }}
            initial={{ scale: 0.85, y: 12 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 320, damping: 24 }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* dial + score */}
            <div className="relative w-[220px] h-[220px] mx-auto">
              {revealed && (
                <motion.div
                  aria-hidden="true"
                  className="absolute pointer-events-none"
                  style={{
                    inset: -30,
                    background:
                      'conic-gradient(from 0deg, rgba(201,164,92,0) 0deg, rgba(201,164,92,0.35) 12deg, rgba(201,164,92,0) 36deg, rgba(201,164,92,0.35) 84deg, rgba(201,164,92,0) 120deg, rgba(201,164,92,0.35) 192deg, rgba(201,164,92,0) 240deg, rgba(201,164,92,0.35) 312deg, rgba(201,164,92,0) 360deg)',
                    WebkitMaskImage: 'radial-gradient(circle, transparent 88px, #000 92px, transparent 150px)',
                    maskImage: 'radial-gradient(circle, transparent 88px, #000 92px, transparent 150px)',
                  }}
                  initial={{ opacity: 0, scale: 0.4, rotate: 0 }}
                  animate={{ opacity: 0.45, scale: 1, rotate: 24 }}
                  transition={{ duration: 0.8, ease: 'easeOut' }}
                />
              )}

              <svg width="220" height="220" viewBox="0 0 200 200" className="absolute inset-0 -rotate-90">
                <circle cx="100" cy="100" r="85" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="6" />
                <circle
                  cx="100"
                  cy="100"
                  r="85"
                  fill="none"
                  stroke="#C9A45C"
                  strokeWidth="6"
                  strokeLinecap="round"
                  strokeDasharray={RING_CIRCUMFERENCE}
                  strokeDashoffset={ringOffset}
                  style={{ filter: 'drop-shadow(0 0 8px rgba(201,164,92,0.6))', transition: 'stroke-dashoffset 0.12s linear' }}
                />
              </svg>

              <div className="absolute inset-0 flex flex-col items-center justify-center">
                {analyzing ? (
                  <>
                    <motion.span
                      className="text-aurum"
                      animate={{ rotate: 360 }}
                      transition={{ repeat: Infinity, duration: 1.1, ease: 'linear' }}
                    >
                      <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
                        <path d="M12 3a9 9 0 1 0 9 9" />
                      </svg>
                    </motion.span>
                    <div className="font-display text-[11px] tracking-[0.26em] text-bone-dim mt-3.5">
                      {COPY.REVEAL_ANALYZING}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex items-baseline gap-1">
                      <span className="font-censor font-semibold text-bone leading-none" style={{ fontSize: 72 }}>
                        {scoreNow.toFixed(1)}
                      </span>
                      <span className="text-[22px] text-bone-dim">/10</span>
                    </div>
                    <div className="font-display text-[11px] tracking-[0.24em] text-aurum mt-1.5">
                      {COPY.REVEAL_SCORE_LABEL}
                    </div>
                  </>
                )}
              </div>
            </div>

            {analyzing && (
              <div className="font-display text-base italic text-bone-dim mt-[22px]">
                {COPY.REVEAL_ANALYZING_NOTE}
              </div>
            )}

            {revealed && (
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, ease: 'easeOut' }}
                className="mt-[22px]"
              >
                <div className="font-display text-xs tracking-[0.28em] text-aurum">
                  {COPY.REVEAL_DONE_HEADLINE}
                </div>
                {(percentileTopText || deltaText) && (
                  <div className="flex gap-2.5 justify-center mt-4 flex-wrap">
                    {percentileTopText && (
                      <span className="inline-flex items-center gap-2 font-display text-xs tracking-[0.1em] text-[#E8E2D6] rounded-full px-3.5 py-2"
                        style={{ background: 'rgba(201,164,92,0.1)', border: '1px solid rgba(201,164,92,0.3)' }}>
                        <LuTrendingUp size={13} className="text-aurum" />
                        {percentileTopText}
                      </span>
                    )}
                    {deltaText && (() => {
                      const tone = DELTA_TONE[deltaDirection] || DELTA_TONE.up;
                      const { Icon } = tone;
                      return (
                        <span
                          data-testid="reveal-delta"
                          data-direction={deltaDirection}
                          className="inline-flex items-center gap-2 font-display text-xs tracking-[0.1em] text-[#E8E2D6] rounded-full px-3.5 py-2"
                          style={{ background: tone.bg, border: `1px solid ${tone.border}` }}
                        >
                          <Icon size={13} style={{ color: tone.color }} />
                          {deltaText}
                        </span>
                      );
                    })()}
                  </div>
                )}
                <div className="font-display text-[15px] italic text-bone-dim mt-4">
                  {COPY.REVEAL_ARCHIVED_NOTE}
                </div>

                <div className="flex flex-col gap-3 mt-[26px]">
                  <button
                    type="button"
                    onClick={onViewScore}
                    className="w-full h-[54px] rounded-field border-0 cursor-pointer flex items-center justify-center gap-3 font-display font-semibold text-sm tracking-[0.22em] text-[#1A1A1A]"
                    style={{ background: 'linear-gradient(180deg, #F0ECE2, #C2BBAC)' }}
                  >
                    <LuLandmark size={17} strokeWidth={1.6} />
                    <span>{COPY.REVEAL_VIEW_SCORE_CTA}</span>
                  </button>
                  <button
                    type="button"
                    onClick={onReviewRoutine}
                    className="w-full h-[54px] rounded-field cursor-pointer flex items-center justify-center gap-3 font-display font-medium text-sm tracking-[0.22em] text-aurum-bright hover:border-aurum-bright transition-colors duration-200"
                    style={{ background: 'transparent', border: '1px solid rgba(201,164,92,0.5)' }}
                  >
                    <LuClipboardList size={17} strokeWidth={1.6} />
                    <span>{COPY.REVEAL_REVIEW_ROUTINE_CTA}</span>
                  </button>
                </div>
                <button
                  type="button"
                  onClick={onDismiss}
                  className="mt-4 bg-transparent border-0 cursor-pointer font-display text-[13px] tracking-[0.16em] text-bone-dim hover:text-bone transition-colors duration-200"
                >
                  {COPY.DISMISS_CTA}
                </button>
              </motion.div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ScanRevealModal;
